import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  CheckCircle, AlertTriangle, Loader2, Database, Shield, 
  Users, FileText, ArrowRight, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const logoUrl = "https://customer-assets.emergentagent.com/job_e65197a9-8183-40fd-a17c-c3a01c1869d3/artifacts/0f0od067_Memorial%20Desk.webp";

export default function SetupMigration() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [status, setStatus] = useState(null);
  const [migrationResults, setMigrationResults] = useState(null);
  const [formData, setFormData] = useState({
    adminEmail: 'memorials@setinstonedidcot.co.uk',
    migrationKey: ''
  });

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/setup/migration-status`);
      setStatus(response.data);
    } catch (error) {
      console.error('Error checking status:', error);
      // Don't show error toast on initial load - might just mean migration hasn't run yet
      setStatus({ is_migrated: false, master_company: null, super_admin: null });
    } finally {
      setLoading(false);
    }
  };

  const runMigration = async (e) => {
    e.preventDefault();
    
    if (!formData.migrationKey) {
      toast.error('Please enter the migration key');
      return;
    }
    
    setMigrating(true);
    setMigrationResults(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/setup/migrate-to-multi-tenant`, {
        admin_email: formData.adminEmail,
        migration_key: formData.migrationKey
      });
      
      setMigrationResults(response.data.results);
      toast.success('Migration completed successfully!');
      
      // Refresh status
      await checkStatus();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Migration failed');
      toast.error(msg);
    } finally {
      setMigrating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 sm:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={logoUrl} alt="Memorial Desk" className="h-12 w-auto mx-auto mb-4" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Database Setup & Migration
          </h1>
          <p className="text-slate-400">
            Configure your Memorial Desk installation
          </p>
        </div>

        {/* Status Card */}
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Database className="w-5 h-5" />
              Migration Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-300">Master Company</span>
                </div>
                {status?.master_company ? (
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm">{status.master_company.name}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-yellow-400">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="text-sm">Not configured</span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-300">Super Admin</span>
                </div>
                {status?.super_admin ? (
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm">{status.super_admin.email}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-yellow-400">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="text-sm">Not configured</span>
                  </div>
                )}
              </div>
              
              <Button 
                variant="outline" 
                onClick={checkStatus}
                className="w-full border-slate-700 text-slate-300"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Status
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Migration Form */}
        {!status?.is_migrated && (
          <Card className="bg-slate-900 border-slate-800 mb-6">
            <CardHeader>
              <CardTitle className="text-white">Run Migration</CardTitle>
              <CardDescription className="text-slate-400">
                This will set up your database for multi-tenant operation. 
                Enter the migration key to proceed.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={runMigration} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Admin Email</Label>
                  <Input
                    value={formData.adminEmail}
                    onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    disabled
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">Migration Key</Label>
                  <Input
                    type="password"
                    value={formData.migrationKey}
                    onChange={(e) => setFormData({ ...formData, migrationKey: e.target.value })}
                    placeholder="Enter migration key"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                  <p className="text-xs text-slate-500">
                    Contact support if you don't have your migration key.
                  </p>
                </div>
                
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <h4 className="font-medium text-blue-300 mb-2">This migration will:</h4>
                  <ul className="text-sm text-blue-200 space-y-1">
                    <li>• Create the master company (Set In Stone Didcot Ltd)</li>
                    <li>• Set your account as Super Admin</li>
                    <li>• Link existing orders and users to your company</li>
                    <li>• Enable all premium features</li>
                  </ul>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={migrating}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                >
                  {migrating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Running Migration...
                    </>
                  ) : (
                    <>
                      <Database className="w-4 h-4 mr-2" />
                      Run Migration
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Migration Results */}
        {migrationResults && (
          <Card className="bg-slate-900 border-slate-800 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Migration Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>Admin Updated:</span>
                  <span className={migrationResults.admin_updated ? 'text-green-400' : 'text-slate-500'}>
                    {migrationResults.admin_updated ? 'Yes' : 'Already done'}
                  </span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Master Company Created:</span>
                  <span className={migrationResults.master_company_created ? 'text-green-400' : 'text-slate-500'}>
                    {migrationResults.master_company_created ? 'Yes' : 'Already exists'}
                  </span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Users Updated:</span>
                  <span className="text-blue-400">{migrationResults.users_updated}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Orders Updated:</span>
                  <span className="text-blue-400">{migrationResults.orders_updated}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Worktop Orders Updated:</span>
                  <span className="text-blue-400">{migrationResults.worktop_orders_updated}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Success State */}
        {status?.is_migrated && (
          <Card className="bg-green-500/10 border-green-500/30 mb-6">
            <CardContent className="pt-6">
              <div className="text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  Setup Complete!
                </h3>
                <p className="text-slate-400 mb-6">
                  Your Memorial Desk installation is ready to use.
                </p>
                <Button 
                  onClick={() => navigate('/login')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                >
                  Go to Login
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-slate-600 text-sm">
          © {new Date().getFullYear()} memorialdesk.co.uk • A product by Set in Stone Didcot
        </p>
      </div>
    </div>
  );
}
